import React, { memo } from 'react';

import { last } from 'lodash-es';
import { useShallow } from 'zustand/react/shallow';

import StandaloneStudentView from '@soomo/lib/components/pageElements/MultipleChoiceQuestionPool/components/StandaloneStudentView';
import {
	getMCQuestionPoolPromptBodyId,
	getMCQuestionPoolId
} from '@soomo/lib/components/pageElements/MultipleChoiceQuestionPool/utils';
import { PreventCopyingProvider } from '@soomo/lib/components/PreventCopying/PreventCopyingProvider';
import type { MCQuestionPoolElement } from '@soomo/lib/types/WebtextManifest';

import * as styles from './styles';
import { useStudyStackStore } from '../store';
import { selectChapterByPageFamilyId } from '../store/selectors';

interface Props {
	index: number;
	numQuestions: number;
	questionPool: MCQuestionPoolElement;
}

const MCQuestionPool: React.FC<Props> = memo((props) => {
	const { index, numQuestions, questionPool } = props;
	const {
		appLoadingState,
		userId,
		userRole,
		saveQuestionPool,
		resetQuestionPool,
		response,
		courseId,
		chapterFamilyId
	} = useStudyStackStore(
		useShallow((state) => ({
			appLoadingState: state.appLoadingState,
			userId: state.userId,
			userRole: state.userRole,
			saveQuestionPool: state.saveQuestionPool,
			resetQuestionPool: state.resetQuestionPool,
			response: last(state.responses[questionPool.family_id])!,
			courseId: state.courseId,
			chapterFamilyId: selectChapterByPageFamilyId(state.chapters, questionPool.page_id)
		}))
	);

	if (appLoadingState !== 'ready') {
		return null;
	}

	const copyingProhibited = userRole !== 'instructor';

	const { resetCount, answer, seed } = response;

	const rootId = getMCQuestionPoolId(questionPool.family_id);
	const promptId = getMCQuestionPoolPromptBodyId(questionPool.family_id);

	return (
		<div css={styles.root} id={rootId}>
			<h5 css={styles.heading}>
				Question {index + 1} of {numQuestions}
			</h5>
			<div css={styles.questionWrapper} role="group" aria-labelledby={promptId}>
				<PreventCopyingProvider copyingProhibited={copyingProhibited}>
					<StandaloneStudentView
						showInstructorView={false}
						userId={userId}
						attemptsAllowed={-1}
						questionPool={questionPool}
						resetCount={resetCount}
						onSave={saveQuestionPool}
						onReset={resetQuestionPool}
						answer={answer}
						promptId={promptId}
						seed={seed}
						saveButtonLabel="Check Answer"
						resetButtonLabel={
							questionPool.questions.length > 1 ? 'Try a Similar Question' : 'Try Again'
						}
						courseId={courseId}
						chapterFamilyId={chapterFamilyId}
						isStudyStack
					/>
				</PreventCopyingProvider>
			</div>
		</div>
	);
});
MCQuestionPool.displayName = 'MCQuestionPool';

export default MCQuestionPool;
